/**
 * Lyst eller mørkt tema.
 *
 * Tre valg, ikke to. «System» er standard og er ikke en høflighet: den som har
 * satt mørk modus på telefonen har alt sagt hva de vil, og en app som spør på
 * nytt overser svaret. De to andre finnes fordi systemvalget ofte er feil for
 * *denne* oppgaven — man planlegger en tur på en lys skjerm om dagen og leser
 * den i bilen om kvelden.
 *
 * Selve fargene ligger i styles.css, ikke her. Dette settet styrer bare hvilket
 * av dem som gjelder, ved å sette `data-theme` på `<html>`.
 */

export type ThemeChoice = "light" | "dark" | "system";

/** Temaet som faktisk vises. «System» løses opp til ett av de to. */
export type ThemeApplied = "light" | "dark";

const STORAGE_KEY = "reise-planer-theme";

const QUERY = "(prefers-color-scheme: dark)";

function isChoice(value: unknown): value is ThemeChoice {
  return value === "light" || value === "dark" || value === "system";
}

/**
 * Hva brukeren har valgt før, eller «system».
 *
 * Leses fra localStorage, som kan kaste i et privat vindu — da er svaret
 * «system», som er riktig standard uansett.
 */
export function storedChoice(): ThemeChoice {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (isChoice(stored)) return stored;
  } catch {
    // Blokkerte informasjonskapsler. Systemvalget gjelder.
  }
  return "system";
}

export function rememberChoice(choice: ThemeChoice): void {
  try {
    localStorage.setItem(STORAGE_KEY, choice);
  } catch {
    // Ikke kritisk; valget gjelder fortsatt for denne økten.
  }
}

/** Hva nettleseren sier at systemet står på. */
export function systemPrefers(): ThemeApplied {
  try {
    return window.matchMedia(QUERY).matches ? "dark" : "light";
  } catch {
    // jsdom og eldre nettlesere mangler matchMedia.
    return "light";
  }
}

/** Valget løst opp til det temaet som skal vises. */
export function resolve(choice: ThemeChoice): ThemeApplied {
  return choice === "system" ? systemPrefers() : choice;
}

/**
 * Setter temaet på dokumentet.
 *
 * `data-theme` settes alltid, også for lyst. Å skrive attributtet framfor å
 * fjerne det gjør at CSS-en har én regel per tema framfor én regel og et
 * fravær — og et fravær kan man ikke skrive en test mot.
 *
 * `color-scheme` følger med fra CSS-en, og er det som gjør at rullefelt,
 * datovelgere og tekstfelt fra nettleseren selv også blir mørke. Uten den er
 * skjemaet lyst midt i en mørk side.
 */
export function apply(choice: ThemeChoice): ThemeApplied {
  const applied = resolve(choice);
  document.documentElement.dataset.theme = applied;
  return applied;
}

/**
 * Følger systemet så lenge brukeren står på «system».
 *
 * Uten dette ville en side som var åpen da telefonen gikk over til nattmodus
 * blitt stående i dagmodus til den ble lastet på nytt.
 */
export function watchSystem(onChange: () => void): () => void {
  try {
    const media = window.matchMedia(QUERY);
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  } catch {
    return () => {};
  }
}
